import { useState } from 'react';
import { FileText, Calendar, Pencil, Check, X, Trash2, MoreVertical } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { IconTooltip } from '../ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { CaseStatusBadge } from './CaseStatusBadge';
import type { Case } from '../../types';
import { formatDate } from '../../utils/formatters';

// Initials for the owner avatar (matches taskline ProjectCard).
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

interface CaseCardProps {
  caseData: Case;
  onClick: (caseId: string) => void;
  onRename?: (caseId: string, newName: string) => void;
  onDelete?: (caseId: string) => void;
}

export function CaseCard({ caseData, onClick, onRename, onDelete }: CaseCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(caseData.name);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleStartEdit = () => {
    setEditName(caseData.name);
    setIsEditing(true);
    setMenuOpen(false);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditName(caseData.name);
    setIsEditing(false);
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    const trimmed = editName.trim();
    if (trimmed && trimmed !== caseData.name) {
      onRename?.(caseData.id, trimmed);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      const trimmed = editName.trim();
      if (trimmed && trimmed !== caseData.name) {
        onRename?.(caseData.id, trimmed);
      }
      setIsEditing(false);
    } else if (e.key === 'Escape') {
      setEditName(caseData.name);
      setIsEditing(false);
    }
  };

  const truncateNotes = (notes: string | undefined, maxLength: number = 100) => {
    if (!notes) return null;
    if (notes.length <= maxLength) return notes;
    return notes.substring(0, maxLength) + '...';
  };

  const hasActions = Boolean(onRename || onDelete);

  return (
    <Card
      className="group h-full min-h-[180px] gap-2 rounded-md cursor-pointer hover:shadow-md transition-shadow duration-200 hover:border-primary/50 overflow-hidden"
      onClick={() => onClick(caseData.id)}
    >
      <CardHeader className="px-3 pt-3 pb-0 overflow-hidden">
        {/* Top row: status, right-aligned */}
        <div className="flex items-center justify-end">
          <CaseStatusBadge status={caseData.status} />
        </div>

        {/* Case name — on its own line, below the status */}
        {isEditing ? (
          <div className="flex items-center gap-1.5 min-w-0" onClick={(e) => e.stopPropagation()}>
            <input
              autoFocus
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={500}
              className="text-base font-semibold border border-border rounded px-2 py-0.5 outline-none focus:border-primary min-w-0 flex-1"
            />
            <IconTooltip label="Save name">
              <button
                onClick={handleSave}
                className="p-1 hover:bg-gray-100 rounded transition-colors shrink-0"
                aria-label="Save name"
              >
                <Check className="h-4 w-4 text-green-600" />
              </button>
            </IconTooltip>
            <IconTooltip label="Cancel editing">
              <button
                onClick={handleCancel}
                className="p-1 hover:bg-gray-100 rounded transition-colors shrink-0"
                aria-label="Cancel editing"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </IconTooltip>
          </div>
        ) : (
          <CardTitle className="text-base font-semibold line-clamp-2 min-w-0" title={caseData.name}>
            {caseData.name}
          </CardTitle>
        )}
      </CardHeader>
      <CardContent className="px-3 pt-0 pb-3 [&:last-child]:pb-3 flex flex-1 flex-col">
        {caseData.notes && (
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
            {truncateNotes(caseData.notes)}
          </p>
        )}
        <div className="mt-auto flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
          <IconTooltip label={caseData.owner_name || caseData.owner}>
            <span className="inline-flex shrink-0 cursor-help">
              <Avatar className="size-6 border border-border">
                <AvatarFallback className="bg-primary/10 text-[10px] font-medium text-primary">
                  {initials(caseData.owner_name || caseData.owner)}
                </AvatarFallback>
              </Avatar>
            </span>
          </IconTooltip>
          <div className="flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" />
            <span>{caseData.document_count} document{caseData.document_count !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            <span>{formatDate(caseData.created_at)}</span>
          </div>
          {!isEditing && hasActions && (
            <Popover open={menuOpen} onOpenChange={setMenuOpen}>
              <PopoverTrigger asChild>
                <button
                  onClick={(e) => e.stopPropagation()}
                  className="ml-auto p-1 hover:bg-gray-100 rounded transition-colors shrink-0 text-gray-400 hover:text-gray-600"
                  aria-label="Case actions"
                  title="Case actions"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="w-40 p-1"
                onClick={(e) => e.stopPropagation()}
              >
                {onRename && (
                  <button
                    onClick={handleStartEdit}
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5 text-gray-500" />
                    Rename
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => { setMenuOpen(false); onDelete(caseData.id); }}
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                )}
              </PopoverContent>
            </Popover>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
